import { forwardRef, useImperativeHandle } from "react";

import EditorLinkIcon from "@atlaskit/icon/glyph/editor/link";

import { Link } from "../../components/Link";
import { Modal } from "../../components/Modal";
import { EMAIL_ADDRESS, PHONE_NUMBER } from "../../constants/general";
import { useModalContext } from "../../contexts/ModalContext/context";
import { useSnackbar } from "../../hooks/useSnackbar";
import useAuth from "../../routes/login/AuthContext";
import { CancelInvitation, CreateInvitation } from "../../services/NetworkApi";
import { Box } from "../../UI/Box";
import { Button } from "../../UI/Button";
import { Typography } from "../../UI/Typography";

interface GeneralInvitationCreationProps {
  l1Id: number;
  l2Id: number;
  l3Id: number;
  l4Id: number;
  renewal: boolean;
  invitationId?: string;
  reloadInvitations: () => void;
}

export const GeneralInvitationCreation = forwardRef<any, GeneralInvitationCreationProps>(
  ({ l1Id, l2Id, l3Id, l4Id, renewal, invitationId, reloadInvitations }, ref) => {
    const { showSuccessSnackbar, showErrorSnackbar } = useSnackbar();
    const { setIsOpen } = useModalContext();
    const { getAuthToken, isSuperAdminRead } = useAuth();

    const onHide = () => setIsOpen(false);

    useImperativeHandle(ref, () => ({
      openModal() {
        setIsOpen(true);
      },
    }));

    const onCreate = async () => {
      try {
        if (invitationId) {
          await CancelInvitation(invitationId, await getAuthToken());
        }

        const invitation = await CreateInvitation(
          "GENERAL",
          l1Id,
          l2Id,
          l3Id,
          l4Id,
          null,
          renewal,
          await getAuthToken()
        );

        if (invitation) {
          reloadInvitations();
          onHide();
          showSuccessSnackbar("Invitation successfuly created");
        } else {
          showErrorSnackbar("An error occured. Please try again");
        }
      } catch (error) {
        showErrorSnackbar("An error occured. Please try again");
      }
    };

    const isNewInvitation = !invitationId;
    const modalTitle = isNewInvitation
      ? "Generate Invite Link"
      : "Deactivate Current Link & Generate New Link";
    const buttonText = isNewInvitation
      ? "Generate Invite Link"
      : "Generate New Invite Link";

    return (
      <Box mt={2}>
        <Modal
          title={modalTitle}
          actions={
            <>
              <Button appearance="subtle" onClick={onHide}>
                Cancel
              </Button>
              <Button iconBefore={<EditorLinkIcon label="Editor link" />} appearance="primary" onClick={onCreate} isDisabled={isSuperAdminRead()}>
                {buttonText}
              </Button>
            </>
          }
        >
          <Box px={3} py={2}>
            {!isNewInvitation && (
              <>
                <Typography variant="body2" gutterBottom>
                  Proceeding will deactivate your current link. If a resident clicks the deactivated link after you complete
                  this step, it will not work.{" "}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>
                    Please update any locations or templates you have saved with your invitation link to the new link after
                    completing this step.
                  </strong>
                </Typography>
              </>
            )}
            {isNewInvitation && (
              <Typography variant="body2" gutterBottom>
                Click the button below to generate a new invite link for the selected options.
              </Typography>
            )}
            <Typography variant="body2" gutterBottom>
              If you have questions about how this tool works, please call us at {PHONE_NUMBER} or e-mail{" "}
              <Link href={`mailto:${EMAIL_ADDRESS}`}>{EMAIL_ADDRESS}</Link>.
            </Typography>
          </Box>
        </Modal>
      </Box>
    );
  }
);
